<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

import type { VendorEvent } from '@/utils/vendors/types'

const props = defineProps<{ event: VendorEvent }>()

const imageUrl = ref<string | null>(null)
const videoUrl = ref<string | null>(null)
const videoFailed = ref(false)

function frontClip(event: VendorEvent): File | null {
  const clips = event.filesToUpload
  return clips.find((f) => /-front\.mp4$/i.test(f.name)) ?? clips[0] ?? null
}

// Rows are virtualised, so this component only exists for rows near the viewport: object URLs are
// created on mount and revoked on unmount.
onMounted(() => {
  const { thumbnailFile } = props.event
  if (thumbnailFile) {
    imageUrl.value = URL.createObjectURL(thumbnailFile)
    return
  }
  const clip = frontClip(props.event)
  if (clip) videoUrl.value = URL.createObjectURL(clip)
})

function onImageError() {
  if (imageUrl.value) URL.revokeObjectURL(imageUrl.value)
  imageUrl.value = null
}

onBeforeUnmount(() => {
  if (imageUrl.value) URL.revokeObjectURL(imageUrl.value)
  if (videoUrl.value) URL.revokeObjectURL(videoUrl.value)
})
</script>

<template>
  <div class="thumb bg-blue-grey-2 row items-center justify-center overflow-hidden">
    <img
      v-if="imageUrl"
      :src="imageUrl"
      class="thumb-media"
      alt=""
      data-testid="tesla-thumb-image"
      @error="onImageError"
    />
    <video
      v-else-if="videoUrl && !videoFailed"
      :src="`${videoUrl}#t=0.1`"
      class="thumb-media"
      preload="metadata"
      muted
      playsinline
      data-testid="tesla-thumb-video"
      @error="videoFailed = true"
    />
    <q-icon v-else name="mdi-video-outline" size="2em" color="blue-grey-4" />
  </div>
</template>

<style scoped>
.thumb {
  width: 120px;
  height: 68px;
  flex: none;
  border-radius: 4px;
}

.thumb-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
